import { z } from 'zod';

import { B2bSavingPlanTypeValues, b2bCompanyPlanTypeToCategory } from '@Common/types/b2b/plans';
import { B2bCashSourceValues } from '@Common/types/bo/b2b/b2b';

import type { B2bSavingPlanCategory, B2bSavingPlanType } from '@Common/types/b2b/plans';
import type { B2bCashSource } from '@Common/types/bo/b2b/b2b';

export const PlanCompartmentTypeValues = [
  'c1_individual_deductible_voluntary_contributions',
  'c1_individual_non_deductible_voluntary_contributions',
  'c2_collective_exempt_contributions',
  'c2_collective_non_exempt_contributions',
  'c3_category_employee_contributions',
  'c3_category_employer_contributions',
  'deductible_article_163_quatervicies',
  'deductible_article_154_bis',
  'non_deductible',
] as const;
export const ZodPlanCompartmentType = z.enum(PlanCompartmentTypeValues);
export type PlanCompartmentType = z.infer<typeof ZodPlanCompartmentType>;

// (backend/src/db/generated_types/enums.ts). DB sub-values collapse into user-facing C2/C3:
//   c1_individual_deductible_voluntary_contributions      -> c1_deductible_voluntary_contributions
//   c1_individual_non_deductible_voluntary_contributions  -> c1_non_deductible_voluntary_contributions
//   c2_collective_exempt_contributions | c2_collective_non_exempt_contributions -> c2_collective_contributions
//   c3_category_employee_contributions | c3_category_employer_contributions      -> c3_mandatory_contributions
export const B2bSavingPlanCompartmentValues = [
  'c1_deductible_voluntary_contributions',
  'c1_non_deductible_voluntary_contributions',
  'c2_collective_contributions',
  'c3_mandatory_contributions',
] as const;
export const ZodB2bSavingPlanCompartment = z.enum(B2bSavingPlanCompartmentValues);
export type B2bSavingPlanCompartment = z.infer<typeof ZodB2bSavingPlanCompartment>;

export const B2bCompartmentalizedPlanCategoryValues = [
  'PER',
  'PERO',
  'PERU',
  'ARTICLE_83',
  'PERECOER',
  'PERECOI',
  'PERECO',
  'PERECOG',
] as const satisfies readonly B2bSavingPlanCategory[];

export const isCompartmentalizedPlan = (
  category: B2bSavingPlanCategory,
): category is (typeof B2bCompartmentalizedPlanCategoryValues)[number] =>
  (B2bCompartmentalizedPlanCategoryValues as readonly B2bSavingPlanCategory[]).includes(category);

export const B2bSavingPlanTypeCompartmentalizedValues: readonly B2bSavingPlanType[] =
  B2bSavingPlanTypeValues.filter((type) =>
    isCompartmentalizedPlan(b2bCompanyPlanTypeToCategory(type)),
  );

export const B2bCompartmentC1CashSource = 'voluntary_deposit' satisfies B2bCashSource;

export const B2bCompartmentC2CashSourceValues: readonly B2bCashSource[] =
  B2bCashSourceValues.filter((cashSource) => cashSource !== B2bCompartmentC1CashSource);
